// frontend/src/pages/SizePage.js
//
// Renders the image-size pages from data/size-pages.json.
//
// MEASURED 2026-08-16. `instagram post size dimensions` is 33,100/mo at KD 17
// with a median of just 13 referring domains across the top four results — the
// best volume-to-resistance ratio found in the whole dataset. The Instagram
// results ranking above it are ordinary Instagram POSTS carrying 1-2 referring
// domains, not the immovable platform pages they looked like from the SERP alone.
//
// THIS IS A TOOL, NOT A GUIDE, AND THAT IS THE WHOLE POINT. Every size keyword
// carries an AI Overview: "what size is an Instagram post" is answered by a
// number, stated directly by Google, and a guide loses the click. What an AI
// Overview cannot do is resize your image — and resizer tools still hold
// positions underneath it (lightxeditor, adobe express resize,
// simpleimageresizer, overvisual). So the tool is the format that survives.
//
// Calibration: our own /blog/instagram-reels-dimensions ranks with ZERO
// referring domains, so a wall of 13 is genuinely reachable.
//
// useLocation, NOT useParams — static routes, no :param.
import '../custom.css'

import React from 'react'
import { Container, Row, Col, Button, Accordion, Image } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import { CheckCircleFill } from 'react-bootstrap-icons'

import Seo, { SITE_URL } from '../components/Seo'
import ImageResizer from '../components/ImageResizer'
import pages from '../data/size-pages.json'
import NotFound from './NotFound'

const HERO = {
  'instagram-post-size': ['/assets/images/articles/size-instagram.webp', 'Photos being edited on a laptop screen'],
  'linkedin-banner-size': ['/assets/images/articles/size-linkedin.webp', 'Someone working at a laptop in an office'],
  'youtube-banner-size': ['/assets/images/articles/size-youtube.webp', 'A video creator\'s filming setup'],
  'facebook-event-cover-photo-size': ['/assets/images/articles/size-facebook.webp', 'A crowd at an event venue'],
}

export default function SizePage () {
  const { pathname } = useLocation()
  const slug = pathname.replace(/^\/+tools\/+/, '').replace(/\/+$/, '')
  const p = pages.find(x => x.slug === slug)
  if (!p) return <NotFound />

  const url = `/tools/${p.slug}`
  const [heroSrc, heroAlt] = HERO[p.slug] || HERO['instagram-post-size']

  return (
    <>
      <Seo
        title={p.seoTitle}
        description={p.seoDescription}
        path={url}
        image={heroSrc}
        jsonLd={[
          {
            '@context': 'https://schema.org', '@type': 'FAQPage',
            mainEntity: p.faqs.map(([q, a]) => ({
              '@type': 'Question', name: q,
              acceptedAnswer: { '@type': 'Answer', text: a },
            })),
          },
          {
            '@context': 'https://schema.org', '@type': 'WebApplication',
            name: `${p.platformLabel} image resizer`,
            url: `${SITE_URL}${url}`,
            applicationCategory: 'MultimediaApplication',
            operatingSystem: 'Any',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          },
        ]}
      />

      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg'>
          <Row className='align-items-center g-4'>
            <Col lg={7}>
              <h1 className='display-5 fw-bold mb-3'>{p.h1}</h1>
              <p className='lead mb-0'>{p.sub}</p>
            </Col>
            <Col lg={5}>
              <Image src={heroSrc} alt={heroAlt} fluid rounded loading='eager'
                     width={1200} height={800} className='shadow-sm' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── THE ANSWER FIRST ─────────
          The AI Overview will already have told them the number. State it
          plainly and immediately anyway — anyone who clicked through wants it
          confirmed, not withheld behind a tool. */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          {/* eslint-disable-next-line react/no-danger */}
          <p className='fs-5' dangerouslySetInnerHTML={{ __html: p.intro }} />
          <p className='text-muted fs-5 mb-0'>{p.intro2}</p>
        </Container>
      </section>

      {/* ───────── THE TABLE ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 900 }}>
          <h2 className='fw-bold mb-4'>Every {p.platformLabel} size</h2>
          <div className='table-responsive mb-3'>
            <table className='table table-bordered align-middle bg-white'>
              <thead className='table-light'>
                <tr>{p.table_head.map(h => <th key={h}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {p.table.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j} className={j === 0 ? 'fw-semibold' : j === 1 ? 'text-primary fw-semibold' : 'text-muted'}
                          style={j === 1 ? { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } : undefined}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className='text-muted mb-0'>{p.outro}</p>
        </Container>
      </section>

      {/* ───────── THE TOOL — the part an AI Overview cannot replace ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 940 }}>
          <ImageResizer formats={p.formats} platformLabel={p.platformLabel} />
        </Container>
      </section>

      {/* ───────── DEPTH ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          {p.sections.map(([heading, body]) => (
            <div className='mb-4' key={heading}>
              <h2 className='fw-bold h4 mb-2'>{heading}</h2>
              <p className='text-muted mb-0'>{body}</p>
            </div>
          ))}
        </Container>
      </section>

      {/* ───────── WHAT IS GREATLY SOCIAL (standing rule 4) ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 940 }}>
          <Row className='align-items-center g-4'>
            <Col md={6}>
              <h2 className='fw-bold mb-3'>What is Greatly Social?</h2>
              <p className='text-muted'>
                Greatly Social is a social media scheduler and content calendar. At Greatly
                Social you can crop an image to each network's ratio inside the post editor and
                schedule it to all eight — Instagram, TikTok, Facebook, LinkedIn, YouTube,
                Pinterest, Threads and X — without resizing, downloading and re-uploading for
                each one.
              </p>
              <p className='text-muted'>
                Plans start at $9/month and the trial needs no card.
              </p>
              <Button variant='primary' href='/register' className='me-2'>
                Start using Greatly Social free
              </Button>
              <Button variant='outline-primary' href='/plans'>See plans</Button>
            </Col>
            <Col md={6}>
              {/* A real capture of the product, not stock — standing rule 2. */}
              <Image src='/assets/logo/videoeditor.webp' alt='Cropping an image in the Greatly Social editor'
                     fluid rounded className='shadow-sm' loading='lazy' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── CROSS-LINKS ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold h4 mb-3'>More free tools</h2>
          <Row className='g-3'>
            <Col md={4}>
              <a href='/tools/instagram-caption-generator' className='d-block p-3 rounded-3 text-decoration-none h-100'
                 style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                <div className='fw-semibold'>Caption generator</div>
                <div className='text-muted small'>Every network at once</div>
              </a>
            </Col>
            <Col md={4}>
              <a href='/tools/instagram-bio-generator' className='d-block p-3 rounded-3 text-decoration-none h-100'
                 style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                <div className='fw-semibold'>Bio generator</div>
                <div className='text-muted small'>Fits the character limit</div>
              </a>
            </Col>
            <Col md={4}>
              <a href='/tools/engagement-rate-calculator' className='d-block p-3 rounded-3 text-decoration-none h-100'
                 style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                <div className='fw-semibold'>Engagement rate calculator</div>
                <div className='text-muted small'>Per-platform formulas</div>
              </a>
            </Col>
            {/* Sibling size pages — added when the second one shipped. Without this
                each new size page relies solely on the footer for inbound links. */}
            {pages.filter(x => x.slug !== p.slug).map(x => (
              <Col md={4} key={x.slug}>
                <a href={`/tools/${x.slug}`} className='d-block p-3 rounded-3 text-decoration-none h-100'
                   style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                  <div className='fw-semibold'>{x.platformLabel} sizes</div>
                  <div className='text-muted small'>Dimensions and a resizer</div>
                </a>
              </Col>
            ))}
            <Col md={4}>
              <a href='/blog/instagram-reels-dimensions' className='d-block p-3 rounded-3 text-decoration-none h-100'
                 style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                <div className='fw-semibold'>Reels dimensions</div>
                <div className='text-muted small'>Sizes and safe zones</div>
              </a>
            </Col>
            <Col md={4}>
              <a href='/best-time-to-post' className='d-block p-3 rounded-3 text-decoration-none h-100'
                 style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                <div className='fw-semibold'>Best time to post</div>
                <div className='text-muted small'>A schedule for your audience</div>
              </a>
            </Col>
            <Col md={4}>
              <a href='/video-photo-editor' className='d-block p-3 rounded-3 text-decoration-none h-100'
                 style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                <div className='fw-semibold'>Video &amp; photo editor</div>
                <div className='text-muted small'>Crop inside the product</div>
              </a>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── PRODUCT ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold mb-3'>Resizing is a step you should not have</h2>
          <p className='text-muted fs-5'>
            At Greatly Social the crop happens where the post is written, and the same post goes
            out to eight networks at the right ratio for each.
          </p>
          <Row className='g-3 mb-4'>
            {[['Crop per network', 'One image, the right ratio everywhere.'],
              ['Schedule once, publish weekly', 'Set your slots and let them run.'],
              ['A caption per network', 'Written once, adapted per platform.'],
              ['See the gaps', 'Empty days are obvious on a calendar, invisible in a list.']].map(([t, b]) => (
              <Col md={6} key={t}>
                <div className='d-flex align-items-start gap-2'>
                  <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                  <div><div className='fw-semibold'>{t}</div><div className='text-muted small'>{b}</div></div>
                </div>
              </Col>
            ))}
          </Row>
          <Button variant='primary' size='lg' href='/register' className='me-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-primary' size='lg' href='/plans'>See plans from $9/month</Button>
        </Container>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4 text-center'>Common questions</h2>
          <Accordion flush defaultActiveKey='0'>
            {p.faqs.map(([q, a], i) => (
              <Accordion.Item eventKey={String(i)} key={i}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body className='text-muted'>{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* ───────── CTA ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto px-4' style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-3'>At Greatly Social you can crop and schedule in one place</h2>
          <p className='lead mb-4'>
            No resizing, downloading and re-uploading for every network. Free for 14 days, no
            card, plans from $9/month.
          </p>
          <Button variant='light' size='lg' href='/register' className='me-2 mb-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-light' size='lg' href='/video-photo-editor' className='mb-2'>
            See the editor
          </Button>
        </div>
      </section>
    </>
  )
}
